import { toast } from 'dashboard/helper/toast';

// Toast IDs persistem entre callbacks pra que o mesmo toast transicione
// loading → success/error (em vez de empilhar 3 toasts diferentes).
// Map separado por label porque ACI e WhatsApp SIP têm registros independentes
// que podem coexistir.
const registrationToastIds = new Map();
const callToastIds = new Map();

const dismissRegistrationToast = label => {
  const id = registrationToastIds.get(label);
  if (id != null) {
    toast.dismiss(id);
    registrationToastIds.delete(label);
  }
};

const dismissCallToast = label => {
  const id = callToastIds.get(label);
  if (id != null) {
    toast.dismiss(id);
    callToastIds.delete(label);
  }
};

/**
 * Mostra/atualiza o toast persistente do ciclo de registro SIP.
 *
 * Eventos esperados (vindos do *SipClient.onUserAgentAction):
 *   - connected           — WebSocket abriu, ainda não registrou
 *   - registered          — REGISTER 200 OK, pronto pra receber chamadas
 *   - unregistered        — REGISTER expirou ou foi cancelado
 *   - registrationFailed  — REGISTER 401/403/etc
 *   - disconnected        — WebSocket caiu
 *
 * @param {object} event - { type, payload? }
 * @param {string} label - prefixo do toast (ex: 'WhatsApp SIP' ou 'SIP')
 */
export function notifyRegistrationEvent(event, label = 'SIP') {
  if (!event?.type) return;

  switch (event.type) {
    case 'connected': {
      // WebSocket OK; ainda esperando REGISTER. Cria/atualiza toast de loading.
      const existingId = registrationToastIds.get(label);
      const newId = toast.loading(`${label}: conectando ao Asterisk...`, {
        id: existingId,
      });
      registrationToastIds.set(label, newId);
      break;
    }

    case 'registered': {
      const existingId = registrationToastIds.get(label);
      toast.success(`${label}: registrado e pronto pra receber chamadas`, {
        id: existingId,
      });
      registrationToastIds.delete(label);
      break;
    }

    case 'unregistered': {
      const existingId = registrationToastIds.get(label);
      toast.warning(`${label}: desregistrado — não receberá chamadas`, {
        id: existingId,
      });
      registrationToastIds.delete(label);
      break;
    }

    case 'registrationFailed': {
      const reason =
        event.payload?.error?.message ||
        event.payload?.message ||
        'verifique credenciais e WebSocket';
      const existingId = registrationToastIds.get(label);
      toast.error(`${label}: falha ao registrar — ${reason}`, {
        id: existingId,
      });
      registrationToastIds.delete(label);
      break;
    }

    case 'disconnected': {
      const existingId = registrationToastIds.get(label);
      toast.error(`${label}: conexão WebSocket caiu — tentando reconectar`, {
        id: existingId,
      });
      registrationToastIds.delete(label);
      break;
    }

    default:
      // Outros eventos (newRTCSession, etc) não geram toast.
      break;
  }
}

/**
 * Mostra/atualiza o toast persistente de uma chamada em andamento.
 *
 * Eventos esperados (vindos do *SipClient.emitCallEvent):
 *   - progress            — INVITE 180/183 Ringing
 *   - accepted/confirmed  — INVITE 200 OK + ACK
 *   - rejected            — INVITE 4xx final (404 = número inválido/sem DDD)
 *   - failed              — erro local ao enviar o INVITE
 *   - ended               — BYE de qualquer lado
 *
 * @param {object} event - { type, cause?, error? }
 * @param {string} label - prefixo do toast (ex: 'Chamada SIP')
 */
export function notifyCallEvent(event, label = 'Chamada') {
  if (!event?.type) return;

  switch (event.type) {
    case 'progress': {
      const existingId = callToastIds.get(label);
      const newId = toast.loading(`${label}: chamando...`, { id: existingId });
      callToastIds.set(label, newId);
      break;
    }

    case 'accepted':
    case 'confirmed': {
      // accepted + confirmed são emitidos pro MESMO 200 OK. Reusa o id (em vez
      // de deletar) pra os dois caírem no mesmo balão em vez de empilhar dois
      // toasts "conectada". O `ended` depois dispensa via dismissCallToast.
      const existingId = callToastIds.get(label);
      const newId = toast.success(`${label}: conectada`, { id: existingId });
      callToastIds.set(label, newId);
      break;
    }

    case 'failed': {
      const reason =
        event.cause ||
        event.error ||
        event.payload?.cause ||
        'erro desconhecido';
      const existingId = callToastIds.get(label);
      toast.error(`${label}: falhou — ${reason}`, { id: existingId });
      callToastIds.delete(label);
      break;
    }

    case 'rejected': {
      // Resposta final negativa do INVITE. 404 Not Found / 484 Address
      // Incomplete / 604 Does Not Exist = número sem rota no Asterisk
      // (inválido ou sem DDD) — mensagem clara em vez do balão genérico.
      const statusCode = event.payload?.statusCode;
      const existingId = callToastIds.get(label);
      callToastIds.delete(label);
      if ([404, 484, 604].includes(statusCode)) {
        toast.error(
          'Número inválido — verifique se incluiu o DDD e tente novamente.',
          { id: existingId }
        );
      } else if ([486, 487, 600, 603].includes(statusCode)) {
        // Ocupado/recusada pelo destino, ou 487 = resposta ao nosso próprio
        // CANCEL (atendente desligou): não é falha, só dispensa o loading.
        if (existingId != null) toast.dismiss(existingId);
      } else {
        // Demais recusas (480/503/408...) mostram o código: é a evidência
        // que o suporte VoIP usa pra rastrear falha de rota intermitente.
        const reason = event.payload?.reason ? ` ${event.payload.reason}` : '';
        toast.error(`${label}: não completou — ${statusCode}${reason}`, {
          id: existingId,
        });
      }
      break;
    }

    case 'ended': {
      // Não emite toast de sucesso (poluiria); só dispensa o loading se ainda existir.
      dismissCallToast(label);
      break;
    }

    default:
      break;
  }
}

/**
 * Chamada recusada automaticamente porque o agente estava em pausa. Fica na
 * tela até ser fechado: quem volta da pausa precisa saber quem tentou ligar.
 *
 * @param {object} payload - { number, displayName }
 */
export function notifyIncomingRejectedWhilePaused(payload = {}) {
  const { number, displayName } = payload;
  const caller = displayName ? `${displayName} (${number})` : number;
  toast.warning(`Ligação recusada durante a pausa: ${caller}`, {
    duration: Infinity,
    closeButton: true,
  });
}

/**
 * Limpa todos os toasts pendentes (útil ao fechar o dialer ou logout).
 */
export function clearAllSipToasts() {
  registrationToastIds.forEach((_id, label) => dismissRegistrationToast(label));
  callToastIds.forEach((_id, label) => dismissCallToast(label));
}
